/**
 * Logout Splash Screen Component
 * Beautiful farewell screen with LifeScribe branding
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Fade,
  Slide,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LogoutOutlined as LogoutIcon,
  CheckCircleOutlined as CheckIcon,
  AutorenewOutlined as LoadingIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface LogoutSplashProps {
  onComplete?: () => void;
}

const LogoutSplash: React.FC<LogoutSplashProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const { theme } = useSelector((state: RootState) => state.ui);
  
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const logoutSteps = [
    { label: 'Clearing session data...', duration: 800 },
    { label: 'Invalidating tokens...', duration: 600 },
    { label: 'Securing your account...', duration: 700 },
    { label: 'Cleanup complete!', duration: 400 },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(timer);
          setShowSuccess(true);
          
          // Auto-redirect after showing success
          setTimeout(() => {
            if (onComplete) {
              onComplete();
            } else {
              window.location.href = '/auth/login?provider=google';
            }
          }, 1500);
          
          return 100;
        }
        return prevProgress + 1;
      });
    }, 25); // Complete in ~2.5 seconds

    return () => clearInterval(timer);
  }, [navigate, onComplete]);

  useEffect(() => {
    const stepTimer = setInterval(() => {
      setCurrentStep((prevStep) => {
        if (prevStep >= logoutSteps.length - 1) {
          clearInterval(stepTimer);
          return prevStep;
        }
        return prevStep + 1;
      });
    }, 650);

    return () => clearInterval(stepTimer);
  }, []);

  const LifeScribeLogo = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 4,
      }}
    >
      {/* Logo Container */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            },
            '50%': {
              transform: 'scale(1.05)',
              boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
            },
            '100%': {
              transform: 'scale(1)',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            },
          },
        }}
      >
        <img 
          src="/logo.png" 
          alt="LifeScribe Logo"
          style={{
            width: '50px',
            height: '50px',
            objectFit: 'contain',
            filter: 'brightness(0) invert(1)', // Make logo white
          }}
        />
      </Box>

      {/* Brand Name */}
      <Typography
        variant="h5"
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontWeight: 600,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textAlign: 'center',
          mb: 0.5,
        }}
      >
        LifeScribe
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
          letterSpacing: '0.5px',
          textAlign: 'center',
        }}
      >
        Admin Dashboard
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme === 'dark' 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        p: 3,
      }}
    >
      <Fade in timeout={800}>
        <Paper
          elevation={theme === 'dark' ? 8 : 4}
          sx={{
            p: 6,
            borderRadius: 4,
            maxWidth: 480,
            width: '100%',
            textAlign: 'center',
            backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff',
            boxShadow: theme === 'dark' 
              ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              : '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
            border: theme === 'dark' ? '1px solid #334155' : '1px solid #e2e8f0',
          }}
        >
          <LifeScribeLogo />

          {!showSuccess ? (
            <Slide direction="up" in timeout={600}>
              <Stack spacing={4}>
                {/* Thank you message */}
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 600,
                      mb: 2,
                      color: 'text.primary',
                    }}
                  >
                    Thank You!
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      fontSize: '1.1rem',
                    }}
                  >
                    Thank you for using the LifeScribe Admin Dashboard. 
                    Your session is being securely terminated.
                  </Typography>
                </Box>

                {/* Logout Icon */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'rotate 2s ease-in-out infinite',
                      '@keyframes rotate': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  >
                    <LoadingIcon sx={{ fontSize: 30, color: 'primary.main' }} />
                  </Box>
                </Box>

                {/* Progress bar */}
                <Box sx={{ width: '100%', mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'action.hover',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mt: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    {Math.round(progress)}% Complete
                  </Typography>
                </Box>

                {/* Current step */}
                <Fade in key={currentStep} timeout={400}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      minHeight: 20,
                    }}
                  >
                    {logoutSteps[currentStep]?.label || ''}
                  </Typography>
                </Fade>
              </Stack>
            </Slide>
          ) : (
            <Fade in timeout={600}>
              <Stack spacing={3} alignItems="center">
                {/* Success Icon */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    animation: 'successPulse 0.6s ease-out',
                    '@keyframes successPulse': {
                      '0%': { transform: 'scale(0.8)', opacity: 0.8 },
                      '50%': { transform: 'scale(1.1)', opacity: 1 },
                      '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                  }}
                >
                  <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 600,
                    color: 'success.main',
                    mb: 1,
                  }}
                >
                  Logout Complete
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  You have been successfully logged out. 
                  Redirecting to login...
                </Typography>
              </Stack>
            </Fade>
          )}

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  display: 'inline-block',
                  animation: 'blink 1.5s ease-in-out infinite',
                  '@keyframes blink': {
                    '0%, 50%': { opacity: 1 },
                    '25%, 75%': { opacity: 0.3 },
                  },
                }}
              />
              Secured by LifeScribe © 2025
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default LogoutSplash;